import React, { useState, useEffect, useRef, useCallback } from "react";
import { Device } from "@twilio/voice-sdk";
import mqtt from "mqtt";  
import config from "./config";  

const CallComponent = ({ phoneNumber, onCallStatusChange, onMqttMessage }) => {
  const [device, setDevice] = useState(null);
  const [callInProgress, setCallInProgress] = useState(false);
  const [localMessage, setLocalMessage] = useState("初始化 Twilio...");
  const [mqttMessage, setMqttMessage] = useState("等待 MQTT 訊息...");
  const [callDuration, setCallDuration] = useState(0);
  const connectionRef = useRef(null);
  const durationIntervalRef = useRef(null);

  // ✅ 更新父組件的 Twilio 通話狀態
  useEffect(() => {
    if (onCallStatusChange) {
      onCallStatusChange(localMessage);
    }
  }, [localMessage, onCallStatusChange]);

  // ✅ 初始化 MQTT 連線並訂閱 `twilio/remote/event`
  useEffect(() => {
    const client = mqtt.connect(config.brokerURL, {
      username: config.username,
      password: config.password,
      reconnectPeriod: 1000, 
    });

    client.on("connect", () => {
      console.log("✅ 已連線至 MQTT");
      client.subscribe("twilio/remote/event", (err) => {
        if (!err) console.log("📡 訂閱成功: twilio/remote/event");
      });
    });

    client.on("message", (topic, message) => {
      if (topic === "twilio/remote/event") {
        const msg = message.toString();
        console.log(`📩 MQTT 訊息: ${msg}`);
        setMqttMessage(msg);
        if (onMqttMessage) {
          onMqttMessage(msg); // ✅ 將 MQTT 訊息傳回父組件
        }
      }
    });

    client.on("error", (error) => {
      console.error("🚨 MQTT 錯誤:", error);
      setMqttMessage("MQTT 連線錯誤");
    });

    return () => client.end();
  }, [onMqttMessage]);

  const initializeTwilioDevice = useCallback(async () => {
    try {
      const response = await fetch("https://nodered2.dtix.tw/token2");
      const data = await response.json();
      if (!data.token) throw new Error("❌ 無效的 Token");

      const twilioDevice = new Device(data.token, { logLevel: 4 });

      twilioDevice.on("registered", () => setLocalMessage("📞 Twilio 設備已註冊"));
      twilioDevice.on("error", (error) => setLocalMessage(`🚨 Twilio 錯誤: ${error.message}`));
      twilioDevice.on("disconnect", () => setLocalMessage("🔌 Twilio 連線中斷"));

      twilioDevice.on("connect", (connection) => {
        setLocalMessage("✅ 通話已接通");
        connection.on("disconnect", () => setLocalMessage("❌ 通話已掛斷"));
      });

      await twilioDevice.register();
      setDevice(twilioDevice);
    } catch (error) {
      setLocalMessage(`❌ 初始化 Twilio 失敗: ${error.message}`);
    }
  }, []);

  useEffect(() => {
    initializeTwilioDevice();
  }, [initializeTwilioDevice]);

  // ✅ 開始計時
  const startCallTimer = () => {
    setCallDuration(0);
    durationIntervalRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  };

  // ✅ 停止計時
  const stopCallTimer = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  };

  // ✅ 撥打電話
  const makeCall = async () => {
    if (!device) return setLocalMessage("Twilio 裝置尚未初始化");
    if (!phoneNumber) return setLocalMessage("請提供電話號碼");

    try {
      const connection = await device.connect({ params: { To: phoneNumber } });
      connectionRef.current = connection;
      setCallInProgress(true);
      setLocalMessage("📞 撥號中...");

      connection.on("accept", () => {
        setLocalMessage("✅ 通話中...");
        startCallTimer();
      });

      connection.on("disconnect", () => {
        setCallInProgress(false);
        setLocalMessage("❌ 通話已掛斷");
        stopCallTimer();
      });

    } catch (error) {
      setLocalMessage(`🚨 通話錯誤: ${error.message}`);
    }
  };

  // ✅ 掛斷電話
  const hangUp = () => {
    if (connectionRef.current) {
      connectionRef.current.disconnect();
      setCallInProgress(false);
      setLocalMessage("❌ 通話已掛斷");
      stopCallTimer();
    }
  };

  return (
    <div className="container text-center mt-5">
      <h3>電話通話</h3>
      <hr />

      {/* 📩 MQTT 訊息區塊 */}
      <div className="alert alert-primary">
        <strong> MQTT 狀態:</strong> {mqttMessage}
      </div>

      {/* 📞 Twilio 訊息區塊 */}
      <div className="alert alert-secondary">
        <strong> Twilio 狀態:</strong> {localMessage}
      </div>

      {callInProgress && (
        <p className="fw-bold">通話時間: {callDuration} 秒</p>
      )}

      <button className="btn btn-success w-100" onClick={makeCall} disabled={callInProgress}>
        撥打 {phoneNumber}
      </button>
      <button className="btn btn-danger w-100 mt-2" onClick={hangUp} disabled={!callInProgress}>
        掛斷
      </button>
    </div>
  );
};

export default CallComponent;
